import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { useState } from 'react';
import { useUpdateBlockMutation } from '../../../../../services/entities/impl/blfApi/endpoints/blocks';
import { apiErrorResponse } from '../../../../../services/entities/impl/blfApi/schemas/apiResponse';
import { Block } from '../../../../../services/entities/impl/blfApi/schemas/block';

/**
 * Custom hook for submitting a block map item update. Manages the submission state,
 * including any error messages and the snackbar's visibility. It encapsulates the
 * logic for updating a block through the API and handling success or failure. Allows
 * external components to control the error message and snackbar visibility.
 *
 * @returns An object containing:
 * - `blockSubmitHandler`: Function to submit the block update.
 * - `snackbarOpen`: Boolean indicating if the snackbar is open.
 * - `setSnackbarOpen`: Function to set the snackbar visibility state.
 * - `errorMessage`: String with the current error message, if any.
 * - `setErrorMessage`: Function to set the current error message.
 */
const useSubmitBlockMapItem = () => {
  const [errorMessage, setErrorMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const [handleUpdate] = useUpdateBlockMutation();

  /**
   * Handles the submission of a block update. Upon calling, it attempts to update
   * the block through the API. It sets the appropriate error messages and controls
   * the visibility of feedback UI elements based on the outcome of the operation.
   *
   * @param {Block} block - The block object to be updated.
   */
  async function blockSubmitHandler(block: Block) {
    try {
      setErrorMessage('');

      const res = await handleUpdate(block);

      if ('error' in res) {
        console.debug('API error: ', res.error);
        let errorMessage = 'Unknown error';

        if ((res.error as FetchBaseQueryError).data !== undefined) {
          errorMessage = (
            (res.error as FetchBaseQueryError).data as apiErrorResponse
          ).detail as string;
          if (typeof errorMessage !== 'string') {
            errorMessage = 'unknown error';
          }
        }
        throw new Error(errorMessage as string);
      }

      // clear status after 3 seconds
      setTimeout(() => {
        setSnackbarOpen(false);
      }, 3000);
    } catch (error) {
      console.error('Caught error: ', error);

      setErrorMessage((error as Error).message || 'Unknown error');

      // in error case give user 10 seconds to understand what happened
      setTimeout(() => {
        setSnackbarOpen(false);
      }, 10000);
    } finally {
      setSnackbarOpen(true);
    }
  }

  return {
    blockSubmitHandler,
    snackbarOpen,
    setSnackbarOpen,
    errorMessage,
    setErrorMessage,
  };
};

export default useSubmitBlockMapItem;
